/**
 * Tendril UI-action executor.
 *
 * Handles `ui_action` directives returned by the Tendril agent loop:
 *   - `{ kind: 'navigate', route, params }` — looks up the React Router path
 *     from view-route-registry and calls navigate() with resolved params.
 *   - Dispatches a `tendril:highlight` CustomEvent for highlight targets.
 *
 * Called by ShowMeChip when the user clicks "Show me →".
 *
 * Security: route lookup is from a local registry (allowlist) — user input
 * cannot inject arbitrary paths.
 */

import viewRouteRegistry from './view-route-registry.js';

/**
 * Resolve a route template with params.
 * e.g. `/workshops/:workshopId` + { workshopId: 'ws-1' } → `/workshops/ws-1`
 *
 * @param {string} template - React Router path template
 * @param {Record<string, string>} params - Param values to substitute
 * @returns {string} Resolved path
 */
function resolveRoute(template, params = {}) {
  let resolved = template;
  for (const [key, value] of Object.entries(params)) {
    resolved = resolved.replace(`:${key}`, encodeURIComponent(value));
  }
  return resolved;
}

/**
 * Execute a ui_action directive.
 *
 * @param {{ kind: string; route?: string; params?: Record<string, string>; target?: string }} action
 * @param {(path: string) => void} navigate - React Router navigate function
 */
export function executeUiAction(action, navigate) {
  if (!action || typeof action.kind !== 'string') {
    return;
  }

  if (action.kind === 'navigate') {
    const routeTemplate = viewRouteRegistry[action.route];
    if (!routeTemplate) {
      // Unknown route: fail-safe (do nothing)
      return;
    }
    const resolvedPath = resolveRoute(routeTemplate, action.params ?? {});
    navigate(resolvedPath);
    return;
  }

  if (action.kind === 'tendril:highlight') {
    // Dispatch a CustomEvent that interested components can listen for
    window.dispatchEvent(
      new CustomEvent('tendril:highlight', {
        detail: { target: action.target },
      })
    );
    return;
  }

  // Unknown action kind — silently ignore (extensibility)
}
